
import { encodeHtmlContent } from '@core/operators';
import * as JSZip from 'jszip';
import { Observable } from 'rxjs';

const xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const start: number = 'A'.charCodeAt(0);
const range: number = 'Z'.charCodeAt(0) - start + 1;

function obj2xml<T>(name: string, properties?: T, orders?: { [key: string]: string[] }): string {

  let xml = '';
 
  if (properties) {

    if (Array.isArray(properties)) {

      (properties as any).forEach(i => xml += obj2xml(name, i, orders));
    }
    else {

      orders = orders ? orders : {};

      let children: { name: string, properties: any }[] = [];
      xml += '<' + name;

      let order = orders[name] ? orders[name] : [];

      order.forEach(prop => {

        if (typeof properties[prop] == 'undefined') return;

        if (typeof properties[prop] != 'object') {
          xml += ' ' + prop + '="' + properties[prop] + '"';
        }
        else {
          children.push({ name: prop, properties: properties[prop]});
        }
      });
      
      for (let prop in properties) {

        if (order.contains(prop)) continue;

        if (typeof properties[prop] != 'object') {
          xml += ' ' + prop + '="' + properties[prop] + '"';
        }
        else {
          children.push({ name: prop, properties: properties[prop] });
        }
      }      

      if (children.length > 0) {

        xml += '>';

        children.forEach(i => xml += obj2xml(i.name, i.properties, orders));

        xml += '</' + name + '>';
      }
      else {
        xml += '/>';
      }
    }
  }
  else {
    xml = '<' + name + '/>';
  }

  return xml;
}

function _(tag: string, props: object, ...contents: string[]): string {

  let properties: { name: string, value: any }[] = [];

  for (let o in props) properties.push({ name: o, value: props[o] });

  return '<' + tag + (properties.length > 0 ? ' ' + properties.map(i => i.name + '="' + i.value + '"').join(' ') : '') + (contents.length > 0 ? '>' + contents.join('') + '</' + tag + '>' : '/>');
}

function files(xlsx: XLSX, sheets: Worksheet[], strings: string[]): any {

  let repeat = (count: number, generator: (index: number) => string): string => {
    let results: string[] = [];
    for (let i = 0; i < count; i++) results.push(generator(i));
    return results.join('');
  }

  let results = {
    '[Content_Types].xml': [xml, 

      obj2xml<Types>("Types", {
        xmlns: "http://schemas.openxmlformats.org/package/2006/content-types",
        Default: [
          { ContentType: "application/xml", Extension: "xml" },
          { ContentType: "application/vnd.openxmlformats-package.relationships+xml", Extension: "rels" }
        ],

        Override: sheets
          .map<ContentType>((i, ix) => ({ ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml", PartName: '/xl/worksheets/sheet' + (ix + 1) + '.xml' }))
          .union([
            { ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml", PartName: "/xl/sharedStrings.xml" },            
            { ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml", PartName: "/xl/styles.xml" },
            { ContentType: "application/vnd.openxmlformats-officedocument.theme+xml", PartName: "/xl/theme/theme1.xml" },
            { ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml", PartName: "/xl/workbook.xml" }
          ])
      })

    ].join(''),

    '_rels/.rels': [xml,

      obj2xml<Relationships>("Relationships", {
        xmlns: "http://schemas.openxmlformats.org/package/2006/relationships",
        Relationship: [
          { Id: "rId1", Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument", Target: "xl/workbook.xml" },
        ]
      })

    ].join(''),

    'xl/_rels/workbook.xml.rels': [xml,

      obj2xml<Relationships>("Relationships", {
        xmlns: "http://schemas.openxmlformats.org/package/2006/relationships",
        Relationship: [
          { Id: "rId1", Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme", Target: "theme/theme1.xml" } as Relationship,
          { Id: "rId2", Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles", Target: "styles.xml" } as Relationship,
          { Id: "rId3", Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings", Target: "sharedStrings.xml" } as Relationship,          
        ].union(sheets.map<Relationship>((i, ix) => ({ Id: 'rId' + (ix + 4), Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet", Target: 'worksheets/sheet' + (ix + 1) + '.xml' })))
      }),

    ].join(''),

    'xl/workbook.xml': [xml,

      obj2xml<workbook>("workbook", {
        'xmlns': "http://schemas.openxmlformats.org/spreadsheetml/2006/main",
        'xmlns:r': "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
        "xmlns:mx": "http://schemas.microsoft.com/office/mac/excel/2008/main",
        "xmlns:mc": "http://schemas.openxmlformats.org/markup-compatibility/2006",
        "xmlns:mv": "urn:schemas-microsoft-com:mac:vml",
        "xmlns:x14": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/main",
        "xmlns:x15": "http://schemas.microsoft.com/office/spreadsheetml/2010/11/main",
        "xmlns:x14ac": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac",
        "xmlns:xm": "http://schemas.microsoft.com/office/excel/2006/main",

        sheets: {
          sheet: sheets.map<sheet>((i, ix) => ({ state: "visible", name: encodeHtmlContent(i.name), sheetId: (ix + 1), "r:id": 'rId' + (ix + 4) }))
        }
      })

    ].join(''),

    'xl/sharedStrings.xml': [xml,

      _('sst', { xmlns: "http://schemas.openxmlformats.org/spreadsheetml/2006/main" },

        repeat(strings.length, i => '<si><t>' + strings[i].replace(/<([a-zA-Z/]+)>/g, '') + '</t></si>')
      )

    ].join(''),

    'xl/styles.xml': [xml,

      obj2xml<styleSheet>('styleSheet',
        {
          "xmlns": "http://schemas.openxmlformats.org/spreadsheetml/2006/main",
          "xmlns:x14ac": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac",
          "xmlns:mc": "http://schemas.openxmlformats.org/markup-compatibility/2006",

          numFmts: {
            count: 3,
            numFmt: [
              { numFmtId: 164, formatCode: xlsx.dateFormat },
              { numFmtId: 4, formatCode: xlsx.decimalFormat },
              { numFmtId: 3, formatCode: xlsx.integerFormat }
            ]
            
          },

          fonts: {
            count: 1,
            font: [
              { sz: { val: "10" }, color: { rgb: 'FF000000' }, name: { val: "Consolas" }, scheme: { val: "minor" } }
            ]
          },

          fills: {
            count: 2,
            fill: [
              { patternFill: { patternType: "none" } },
              { patternFill: { patternType: "lightGray" } }
            ]
          },

          borders: {
            count: 2,
            border: [
              {},
              {
                left: { style: "thin", color: { rgb: 'FF000000' } },
                right: { style: "thin", color: { rgb: 'FF000000' } },
                top: { style: "thin", color: { rgb: 'FF000000' } },
                bottom: { style: "thin", color: { rgb: 'FF000000' } }
              }
            ]
          },

          cellStyleXfs: {
            count: 1,
            xf: [{ borderId: 0, fillId: 0, fontId: 0, applyAlignment: 1, applyFont: 1 }]
          },

          cellXfs: {
            count: 37,
            xf: [
              { xfId: 0, fontId: 0, fillId: 0, borderId: 0, numFmtId: 0 },

              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "left", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "center", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "right", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "center" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "center" } },

              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "left", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "center", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "right", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "top" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "top" } },

              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "left", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "center", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 0, applyAlignment: 1, alignment: { horizontal: "right", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 164, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 4, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "left", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "center", vertical: "bottom" } },
              { xfId: 0, fontId: 0, fillId: 0, borderId: 1, numFmtId: 3, applyAlignment: 1, applyNumberFormat: 1, alignment: { horizontal: "right", vertical: "bottom" } },
            
            ]
          },

          cellStyles: {
            count: 1,
            cellStyle: [{ xfId: 0, name: "Normal", builtinId: 0 }]
          },

          dxfs: { count: 0 }
        },
        {
          'styleSheet': ['numFmts', 'fonts', 'fills', 'borders', 'cellStyleXfs', 'cellXfs', 'cellStyles'],                    
          
        })
    ].join(''),

    'xl/theme/theme1.xml': [xml, '<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" name="Sheets"><a:themeElements><a:clrScheme name="Sheets"><a:dk1><a:srgbClr val="000000"/></a:dk1><a:lt1><a:srgbClr val="FFFFFF"/></a:lt1><a:dk2><a:srgbClr val="000000"/></a:dk2><a:lt2><a:srgbClr val="FFFFFF"/></a:lt2><a:accent1><a:srgbClr val="4285F4"/></a:accent1><a:accent2><a:srgbClr val="EA4335"/></a:accent2><a:accent3><a:srgbClr val="FBBC04"/></a:accent3><a:accent4><a:srgbClr val="34A853"/></a:accent4><a:accent5><a:srgbClr val="FF6D01"/></a:accent5><a:accent6><a:srgbClr val="46BDC6"/></a:accent6><a:hlink><a:srgbClr val="1155CC"/></a:hlink><a:folHlink><a:srgbClr val="1155CC"/></a:folHlink></a:clrScheme><a:fontScheme name="Sheets"><a:majorFont><a:latin typeface="Consolas"/><a:ea typeface="Consolas"/><a:cs typeface="Consolas"/></a:majorFont><a:minorFont><a:latin typeface="Consolas"/><a:ea typeface="Consolas"/><a:cs typeface="Consolas"/></a:minorFont></a:fontScheme><a:fmtScheme name="Office"><a:fillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:lumMod val="110000"/><a:satMod val="105000"/><a:tint val="67000"/></a:schemeClr></a:gs><a:gs pos="50000"><a:schemeClr val="phClr"><a:lumMod val="105000"/><a:satMod val="103000"/><a:tint val="73000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:lumMod val="105000"/><a:satMod val="109000"/><a:tint val="81000"/></a:schemeClr></a:gs></a:gsLst><a:lin ang="5400000" scaled="0"/></a:gradFill><a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:satMod val="103000"/><a:lumMod val="102000"/><a:tint val="94000"/></a:schemeClr></a:gs><a:gs pos="50000"><a:schemeClr val="phClr"><a:satMod val="110000"/><a:lumMod val="100000"/><a:shade val="100000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:lumMod val="99000"/><a:satMod val="120000"/><a:shade val="78000"/></a:schemeClr></a:gs></a:gsLst><a:lin ang="5400000" scaled="0"/></a:gradFill></a:fillStyleLst><a:lnStyleLst><a:ln w="6350" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln><a:ln w="12700" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln><a:ln w="19050" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln></a:lnStyleLst><a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst><a:outerShdw blurRad="57150" dist="19050" dir="5400000" algn="ctr" rotWithShape="0"><a:srgbClr val="000000"><a:alpha val="63000"/></a:srgbClr></a:outerShdw></a:effectLst></a:effectStyle></a:effectStyleLst><a:bgFillStyleLst><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"><a:tint val="95000"/><a:satMod val="170000"/></a:schemeClr></a:solidFill><a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="93000"/><a:satMod val="150000"/><a:shade val="98000"/><a:lumMod val="102000"/></a:schemeClr></a:gs><a:gs pos="50000"><a:schemeClr val="phClr"><a:tint val="98000"/><a:satMod val="130000"/><a:shade val="90000"/><a:lumMod val="103000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:shade val="63000"/><a:satMod val="120000"/></a:schemeClr></a:gs></a:gsLst><a:lin ang="5400000" scaled="0"/></a:gradFill></a:bgFillStyleLst></a:fmtScheme></a:themeElements></a:theme>'].join(''),
  }

  for (let i = 0; i < sheets.length; i++) results['xl/worksheets/sheet' + (i + 1) + '.xml'] = sheets[i].xml;

  return results;
}

function styleOf(cell: Cell, column: Column): number {

  let v = column.valign == 'center' ? 0 : column.valign == 'top' ? 12 : 24;

  if (typeof cell.data == 'string') {
    switch (column.align) {
      case 'left': return 1 + v;
      case 'center': return 2 + v;
      case 'right': return 3 + v;
      default: return 1;
    }
  }

  if (cell.data instanceof Date) {
    switch (column.align) {
      case 'left': return 4 + v;
      case 'center': return 5 + v;
      case 'right': return 6 + v;
      default: return 4;
    }
  }

  if (typeof cell.data == 'number') {
    switch (column.align) {
      case 'left': return v + (column.number == 'auto' ? (cell.data % 1 != 0 ? 7 : 10) : column.number == 'decimal' ? 7 : 10);
      case 'center': return v + (column.number == 'auto' ? (cell.data % 1 != 0 ? 8 : 11) : column.number == 'decimal' ? 8 : 11);
      case 'right': return v + (column.number == 'auto' ? (cell.data % 1 != 0 ? 9 : 12) : column.number == 'decimal' ? 9 : 12);
      default: return 7;
    }
  }

  return 1;
}

function lengthOf(cell: Cell, xlsx: XLSX, column: Column): number {

  if (typeof cell.data == 'undefined' || cell.data == null) return 0;

  switch (typeof cell.data) {
    case 'string': return cell.data.length; 
    case 'number':

      let txt = cell.data.toString().split('.');

      if (cell.data % 1 != 0 || column.number == 'decimal') {
        return txt[0].length + Math.floor(txt[0].length / 3) + xlsx.decimalFormat.length - xlsx.decimalFormat.split('.')[0].length + (cell.data < 0 ? 1 : 0);
      }
      else {
        return txt[0].length + Math.floor(txt[0].length / 3) + xlsx.integerFormat.length - xlsx.integerFormat.split('.')[0].length - 1 + (cell.data < 0 ? 1 : 0);
      }

      

    default: return xlsx.dateFormat.length;
  }
}


function indexOf(col: string): number {
  col = col.toUpperCase();
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result *= range;
    result += (col.charCodeAt(i) - start);
  }

  return result + 1;
}

function colOf(x: number): string {
  let result = '';
  x--;

  do {
    let code = x % range;
    result = String.fromCharCode(code + start) + result;
    x = (x - code) / range;
  }
  while (x > 0);

  return result;
}

function typeOf(cell: Cell): string {

  if (typeof cell.data == 'string') return 's';
  if (typeof cell.data == 'number') return 'n';
  if (cell.data instanceof Date) return 'd';  

  return 's';
}

function valueOf(cell: Cell, xlsx: XLSX): string {
  if (cell.data instanceof Date) return (cell.data as Date).toISOString().replace('Z', '');
  if (typeof cell.data == 'string') return xlsx.indexOf(cell.data as string).toString();
  return typeof cell.data == 'number' ? cell.data.toString() : '';
}


/* Represents a xlsx file */
export class XLSX {

  private _sheets: Worksheet[] = [];
  private _sheetLU: any = {};
  private _shareLU: any = {};
  private _shareUC: number = 0;

  public decimalFormat: string = '##,0.00';
  public integerFormat: string = "##,0";
  public dateFormat: string = 'dd-MM-yyyy';

  /**
   * Retrives or generates a worksheet with a specified name
   * @param name name of the worksheet to be retrived or generated
   */
  public worksheet(name: string): Worksheet {


    if (!this._sheetLU[name]) {
      this._sheetLU[name] = this._sheets.length;
      this._sheets.push(new Worksheet(name, this));
    }

    return this._sheets[this._sheetLU[name]];
  }

  /** Generates xlsx file and returns its contents as a base64 encoded string */
  public generate(): Observable<string> {

    return new Observable<string>(o => {

      let zip = new JSZip();
      let xlsx = files(this, this._sheets, this._shared);

      for (let path in xlsx) {
        zip.file(path, xlsx[path]);
      }

      zip.generateAsync({ type: 'base64', compression: 'DEFLATE' }).then(value => {        
        o.next(value);
      });
    });    
  }

  /**
   * Registers a string to shared string list. This method is designed to be invoked automatically.
   * @param value value to be registered
   */
  public share(value: string) {

    if (!value || value.trim() == '') return;

    value = encodeHtmlContent(value);

    if (!this._shareLU[value]) {
      this._shareLU[value] = { index: this._shareUC++, count: 0 }
    }

    this._shareLU[value].count++;
  }

  /**
   * Unregisters a string from shared string list. This method is designed to be invoked automatically.
   * @param value value to be unregistered
   */
  public unshare(value: string) {

    if (!value || value.trim() == '') return;

    value = encodeHtmlContent(value);

    if (this._shareLU[value]) {
      this._shareLU[value].count--;

      if (this._shareLU[value].count == 0) {

        for (let o in this._shareLU) {
          if (this._shareLU[o].index > this._shareLU[value].index) this._shareLU[o].index--;
        }

        delete this._shareLU[value];        
      }
    }
  }

  /**
   * Index of a string in shared string list.
   * @param value value, index of which to be retrived. If the value is not registered -1 will be returned.
   */
  public indexOf(value: string): number {

    if (!value || value.trim() == '') return -1;

    value = encodeHtmlContent(value);
    return this._shareLU[value] ? this._shareLU[value].index : -1;
  }

  private get _shared(): string[] {
    let results: string[] = [];
    for (let o in this._shareLU) results[this._shareLU[o].index] = o;
    return results;
  }
}

/** Represents a worksheet of xlsx format */
export class Worksheet {

  private _name: string;
  private _cells: Cell[][] = [];
  private _columns: Column[] = [];
  private _merges: string[] = [];
  private _xlsx: XLSX;
  private _frozenTopRowCount: number = 0;

  /**
   * Constructs a worksheet. Instances of this class is designed to be generated automatically by XLSX.worksheet(name: string). Manually created worksheet will not be registered to any instance of XLSX and will not be generated.
   * @param name name of the worksheet
   * @param xlsx xlsx, whichs contains the worksheet.
   */
  constructor(name: string, xlsx: XLSX) {
    this._name = name;
    this._xlsx = xlsx;
  }

  public get frozenTopRowCount(): number {
    return this._frozenTopRowCount;
  }

  public set frozenTopRowCount(value: number) {
    this._frozenTopRowCount = Math.max(value, 0);
  }

  /** Gets name of the worksheet */
  public get name(): string {
    return this._name;
  }

  /** Gets XML contents of the worksheet */
  public get xml(): string {
    return [ xml,

      '<worksheet' +
      ' xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
      ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"' +
      ' xmlns:mx="http://schemas.microsoft.com/office/mac/excel/2008/main"' +
      ' xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"' +
      ' xmlns:mv="urn:schemas-microsoft-com:mac:vml"' +
      ' xmlns:x14="http://schemas.microsoft.com/office/spreadsheetml/2009/9/main"' +
      ' xmlns:x15="http://schemas.microsoft.com/office/spreadsheetml/2010/11/main"' +
      ' xmlns:x14ac="http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac"' +
      ' xmlns:xm="http://schemas.microsoft.com/office/excel/2006/main">',      

      this._sheetViews,
      this._cols,            
      this._sheetData,
      this._mergeCells,      

      '</worksheet>'

    ].join('');
  }

  /**
   * Retrives or generates a cell
   * @param c column's name of the cell (A,B,C...)
   * @param y row's index of the cell (1,2,3,...)
   */
  public cell(c: string | number, y: number): Cell {

    let x = typeof c == 'string' ? indexOf(c) : c as number;

    if (!this._cells[y]) this._cells[y] = [];
    if (!this._cells[y][x]) this._cells[y][x] = new Cell(this, this._xlsx, typeof c == 'string' ? c as string : colOf(x), y);

    return this._cells[y][x];
  }

  /**
   * Tests if a cell is generated or not
   * @param c column's name of the cell (A,B,C,...)
   * @param y row's index of the cell (1,2,3,...)
   */
  public has(c: string | number, y: number): boolean {

    let x = typeof c == 'string' ? indexOf(c) : c as number;

    return typeof this._cells[y] != 'undefined' && typeof this._cells[y][x] != 'undefined' && typeof this._cells[y][x].data != 'undefined' && this._cells[y][x].data != null;
  }

  /**
   * Gets a column
   * @param c name or index of the column
   */
  public column(c: string | number): Column {
    let y = typeof c == 'string' ? indexOf(c as string) : c as number;
    if (!this._columns[y]) this._columns[y] = { align: 'left', valign: 'center', number: 'auto' };
    return this._columns[y];
  }

  /**
   * Fills a row with specified values
   * @param y row's index (1,2,3,...)
   * @param cells values, to be filled into the rows starting from column A
   */
  public row(y: number, ...cells: (string | number | Date)[]) {
    cells.forEach((data, x) => this.cell(colOf(x + 1), y).data = data);
  }

  /**
   * Counts elements of the worksheet
   * @param mode type of elements to be counted
   */
  public count(mode: 'cells' | 'rows' | 'cols'): number {
    switch (mode) {
      case 'cells': return this._cells.skip(1).sum(i => i.length - 1);
      case 'rows': return this._cells.length - 1;
      case 'cols': return this._cells.max(i => i.length - 1);
    }
  }

  /**
   * Merges cells
   * @param from from cell
   * @param to to cell
   */
  public merge(from: string, to: string) {

    if (from == to || this._merges.contains(from + ":" + to)) return;    
    this._merges.push(from + ":" + to);
  }

  private get _cols(): string {
    let cols: number[] = [];

    this._cells.forEach(row => {
      row.forEach((cell, cI) => {
        cols[cI] = Math.max(cols[cI] ? cols[cI] : 0, lengthOf(cell, this._xlsx, this.column(cI)));
      });
    });

    let results = cols.map((i, x) => i ? '<col min="' + x + '" max="' + x + '" width="' + (Math.floor(((i + 2) * 10 + 5) * 256 / 10) / 256) + '"/>' : '').join('');

    return '<cols>' + results + '</cols>';
  }

  private get _sheetData(): string {

    let parts: string[] = ['<sheetData>'];

    this._cells.forEach((row, rI) => {
      parts.push('<row r="' + rI + '">');

      row.forEach((cell, rC) => {

        let hasData = typeof cell.data != 'undefined' && cell.data != null && !(typeof cell.data == 'number' && isNaN(cell.data)) && !(typeof cell.data == 'string' && cell.data.trim() == '');

        if (hasData) {
          parts.push('<c r="' + (colOf(rC) + rI) + '" s="' + styleOf(cell, this.column(rC)) + '" t="' + typeOf(cell) + '"><v>' + valueOf(cell, this._xlsx) + '</v></c>');
        }
        else if (this._merges.length > 0) {
          parts.push('<c r="' + (colOf(rC) + rI) + '" s="' + styleOf(cell, this.column(rC)) + '" t="' + typeOf(cell) + '"><v></v></c>');
        }
        
      });

      parts.push('</row>');
    });

    parts.push('</sheetData>');

    return parts.join('');
  }

  private get _mergeCells(): string {

    if (this._merges.length < 1) return '';

    return '<mergeCells count="' + this._merges.length + '">' + this._merges.map(i => '<mergeCell ref="' + i + '"/>') + '</mergeCells>';
  }

  private get _sheetViews(): string {

    if (this._frozenTopRowCount < 1) return '';

    return '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1.0" topLeftCell="A' + (this._frozenTopRowCount + 1) + '" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>';
  }
}

/** Represents a cell of xlsx format */
export class Cell {

  private _data: string | number | Date = undefined;
  private _xlsx: XLSX;
  private _worksheet: Worksheet;

  private _c: string;
  private _y: number;

  /** Gets or sets data of the cell **/
  public get data(): string | number | Date {
    return this._data;
  }

  /** Gets or sets data of the cell **/
  public set data(value: string | number | Date) {

    if (typeof this._data == 'string') {
      this._xlsx.unshare(this._data);
    }

    this._data = value;
    if (typeof this._data == 'string') {
      this._xlsx.share(this._data);
    }    
  }

  public merge(mode: 'up' | 'down') {

    let y = this._y;

    switch (mode) {
      case 'up':
        while (y > 1 && !this._worksheet.has(this._c, y - 1)) this._worksheet.cell(this._c, --y).data = null;        
        break;

      case 'down':
        while (y < this._worksheet.count('rows') && !this._worksheet.has(this._c, y + 1)) this._worksheet.cell(this._c, ++y).data = null;        
        break;
    }

    if (y < this._y) {
      this._worksheet.cell(this._c, y).data = this.data;
      this.data = null;
    }

    this._worksheet.merge(this._c + this._y, this._c + y);
  }

  /**
   * Constructs a cell. Instances of this class is designed to be generated automatically by Worksheet.cell(c: string, y: number). Manually created cell will not be registered to any instance of XLSX and will not be generated.
   * @param xlsx xlsx, whichs contains the cell.
   */
  constructor(worksheet: Worksheet, xlsx: XLSX, c: string, y: number) {
    this._worksheet = worksheet;
    this._xlsx = xlsx;
    this._c = c;
    this._y = y;
  }
}

export interface Column {
  valign: 'top' | 'bottom' | 'center';
  align: 'left' | 'right' | 'center';
  number: 'decimal' | 'integer' | 'auto';
}

interface Types {
  "xmlns": "http://schemas.openxmlformats.org/package/2006/content-types";
  Default: ContentType[];
  Override: ContentType[];
}

interface ContentType {
  ContentType: string;
  Extension?: string;
  PartName?: string;
}

interface workbook {
  'xmlns': "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
  'xmlns:r': "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
  "xmlns:mx": "http://schemas.microsoft.com/office/mac/excel/2008/main";
  "xmlns:mc": "http://schemas.openxmlformats.org/markup-compatibility/2006";
  "xmlns:mv": "urn:schemas-microsoft-com:mac:vml";
  "xmlns:x14": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/main";
  "xmlns:x15": "http://schemas.microsoft.com/office/spreadsheetml/2010/11/main";
  "xmlns:x14ac": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac";
  "xmlns:xm": "http://schemas.microsoft.com/office/excel/2006/main";

  sheets: sheets;
}

interface sheets {
  sheet: sheet[]
}

interface sheet {
  state: string;
  name: string;
  sheetId: number;
  "r:id": string;
}


interface Relationships {
  xmlns: "http://schemas.openxmlformats.org/package/2006/relationships";
  Relationship: Relationship[];
}

interface Relationship {
  Id: string;
  Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme" | "http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" | "http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" | "http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" | "http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument";
  Target: string;
}

interface styleSheet {

  'xmlns': "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
  "xmlns:x14ac": "http://schemas.microsoft.com/office/spreadsheetml/2009/9/ac";
  "xmlns:mc": "http://schemas.openxmlformats.org/markup-compatibility/2006",

  numFmts?: numFmts;
  fonts?: fonts;
  fills?: fills;
  borders?: borders;
  cellStyleXfs?: cellXfs;
  cellXfs?: cellXfs;
  cellStyles?: cellStyles;
  dxfs?: dxfs;
  tableStyles?: tableStyles;
  extLst?: extLst;
}

interface numFmts {
  count: number;
  numFmt: numFmt[];
}

interface numFmt {
  numFmtId: number;
  formatCode: string;
}

interface fonts {
  
  count: number;
  font: font[];
}

interface font {
  name: val;
  sz?: val;
  color?: color;
  family?: val;
  scheme?: val;
  b?: string;
  i?: string;
}

interface val {
  val: string;
}

interface color {
  rgb?: string;
  theme?: number;
  tint?: string;
  indexed?: number;
}

interface fills {
  count: number;
  fill: fill[];
}

interface fill {
  patternFill: patternFill;
}

interface patternFill {
  patternType: string;
  fgColor?: color;
  bgColor?: color;
}

interface borders {
  count: number;
  border: border[];
}

interface border {
  left?: borderOptions;
  right?: borderOptions;
  top?: borderOptions;
  bottom?: borderOptions;
  diagonal?: borderOptions;
}

interface borderOptions {
  style?: "thick" | "medium" | "thin" | "double";
  color?: color;
}

interface cellXfs {
  count?: number;
  xf: xf[];
}

interface xf {
  borderId?: number;
  fillId?: number;
  fontId?: number; numFmtId?: number;
  xfId?: number;
  applyAlignment?: 0 | 1;
  applyFont?: 0 | 1,
  applyNumberFormat?: 0 | 1;
  applyBorder?: 0 | 1;
  applyProtection?: 0 | 1;
  applyFill?: 0 | 1;
  alignment?: aligment;
}

interface aligment {
  readingOrder?: 0 | 1;
  shrinkToFit?: 0 | 1;
  horizontal?: "left" | "center" | "right";
  vertical?: "top" | "center" | "bottom";
  wrapText?: 0 | 1;
}

interface cellStyles {
  count?: number;
  cellStyle: cellStyle[];
}

interface cellStyle {
  xfId: number;
  name: string;
  builtinId: number;
  customBuiltin?: 0 | 1;
}

interface dxfs {
  count: number;
}

interface tableStyles {
  count: number;
  defaultTableStyle: "TableStyleMedium2";
  defaultPivotStyle: "PivotStyleLight16";
}

interface extLst {
  ext: ext[];
}

interface ext {
  uri: "{EB79DEF2-80B8-43e5-95BD-54CBDDF9020C}" | "{9260A510-F301-46a8-8635-F512D64BE5F5}";

  "xmlns:x14"?: "http://schemas.microsoft.com/office/spreadsheetml/2009/9/main"
  "x14:slicerStyles"?: x14_slicerStyles;

  "xmlns:x15"?: "http://schemas.microsoft.com/office/spreadsheetml/2010/11/main";
  "x15:timelineStyles"?: x15_timelineStyles;

}

interface x14_slicerStyles {
  defaultSlicerStyle: "SlicerStyleLight1";
}

interface x15_timelineStyles {
  defaultTimelineStyle: "TimeSlicerStyleLight1";
}

